import React, { useState } from "react";
import { observer } from "mobx-react";

import { TermsAndConditionsModal } from "@formant/ui-kit";

import { useStores } from "../context/StoreContext";
import { currentLegal } from "../legal";
import { AcceptTerms } from "./AcceptTerms";

interface ICheckTermsAcceptance {
    children?: React.ReactNode;
}
export const CheckTermsAcceptance = observer(function CheckTermsAcceptance(
    props: ICheckTermsAcceptance,
) {
    const { userStore, authStore, organizationStore } = useStores();
    const user = userStore.authenticated();
    const [isAccepting, setIsAccepting] = useState(false);
    const [acceptError, setAcceptError] = useState<string | null>(null);
    const [lastAcceptTime, setLastAcceptTime] = useState(0);

    if (!user) {
        return null;
    }

    const legal = currentLegal({ userStore, organizationStore });

    if (!legal) {
        return <>{props.children}</>;
    }

    const needsAcceptance = user.termsAccepted !== legal.name;
    const { date, termsDocument, privacyPolicyDocument } = legal;

    // Feature flag check - default to true if not set
    const organization = organizationStore.authenticated();
    const useNewModal = organization && organization.termsModalV2Enabled;

    if (needsAcceptance && !isAccepting) {
        if (useNewModal) {
            const handleAccept = async () => {
                const now = Date.now();
                if (now - lastAcceptTime < 1000) {
                    return; // Debounce 1 second
                }

                setLastAcceptTime(now);
                setIsAccepting(true);
                setAcceptError(null);

                try {
                    const { userId } = authStore;
                    if (!userId) {
                        throw new Error("User not authenticated");
                    }

                    await userStore.update(userId, {
                        termsAccepted: legal.name,
                    });

                    // Force refresh user data to ensure cache is updated
                    await userStore.fetchGet(userId);
                } catch (error) {
                    // eslint-disable-next-line no-console
                    console.error("Failed to accept terms:", error);
                    setAcceptError("Failed to accept terms. Please try again.");
                } finally {
                    setIsAccepting(false);
                }
            };

            return (
                <TermsAndConditionsModal
                    isOpen={true}
                    onAccept={handleAccept}
                    onDecline={() => authStore.logout()}
                    firstName={user.firstName}
                    hasAcceptedTerms={Boolean(user.termsAccepted)}
                    updateDate={date}
                    termsUrl={termsDocument}
                    privacyPolicyUrl={privacyPolicyDocument}
                    isLoading={isAccepting}
                    error={acceptError}
                />
            );
        } else {
            return <AcceptTerms />;
        }
    }

    return <>{props.children}</>;
});
